/** Secondary-unit designators from USPS Publication 28 C2, plus "#". */
export const UNIT_DESIGNATORS = new Set([
  "#",
  "APARTMENT",
  "APT",
  "BLDG",
  "BOX",
  "BUILDING",
  "DEPARTMENT",
  "DEPT",
  "FL",
  "FLOOR",
  "LOT",
  "NO",
  "NUM",
  "PH",
  "PO",
  "RM",
  "ROOM",
  "SPACE",
  "SPC",
  "STE",
  "SUITE",
  "TRLR",
  "UNIT",
]);

export function addressTokens(value: string): string[] {
  return value
    .toUpperCase()
    .replace(/\./g, "")
    .replace(/#/g, " # ")
    .replace(/[^A-Z0-9#]+/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean);
}

/** Carriers rewrite the designator (Suite -> APT), so only the identifier is stable. */
export function unitAbsorbedIntoStreet1(
  street1: string,
  street2: string,
): boolean {
  const unit = addressTokens(street2);
  if (unit.length === 0) {
    return false;
  }
  // Skip the house number so "Apt 201" survives against "201 Brakish Run".
  const primary = new Set(addressTokens(street1).slice(1));
  return unit.every(
    (token) => UNIT_DESIGNATORS.has(token) || primary.has(token),
  );
}

/** A PO box is a primary address line, never a unit hanging off a street. */
const PRIMARY_LINE_DESIGNATORS = new Set(["#", "PO", "BOX"]);

/** Longest first so "SUITE" wins the alternation before "STE" can match it. */
function alternation(excluded: Set<string>): string {
  return [...UNIT_DESIGNATORS]
    .filter((designator) => !excluded.has(designator))
    .sort((a, b) => b.length - a.length)
    .join("|");
}

const HASH = new Set(["#"]);

/** "#12" and "APT 12" — a word designator ends at a word boundary, "#" doesn't. */
const WORD_DESIGNATOR = `(?:#|(?:${alternation(HASH)})\\b)`;

/** Stricter: a separator must follow, so "BOX" can't match inside "Boxwood". */
const SEPARATED_DESIGNATOR = `(?:#|(?:${alternation(HASH)})(?=[\\s#.]))`;

/** Excludes the PO-box words, which belong to street1 rather than a unit. */
const UNIT_ONLY_DESIGNATOR = `(?:#|(?:${alternation(
  PRIMARY_LINE_DESIGNATORS,
)})\\b)`;

/** Whatever sits between designator and identifier: "APT 4", "Apt. 4", "APT #4". */
const DESIGNATOR_GAP = "\\s*\\.?\\s*#?\\s*";

const UNIT_IDENTIFIER = "[A-Za-z0-9][A-Za-z0-9-]*";

/** Trailing unit on a street line: "123 Main St Apt 4" -> "Apt 4". */
const SECONDARY_UNIT_SUFFIX = new RegExp(
  `\\s(${SEPARATED_DESIGNATOR}${DESIGNATOR_GAP}${UNIT_IDENTIFIER})\\s*$`,
  "i",
);

/** Whole line is a unit: "Apt 4", "#12". */
const SECONDARY_UNIT_PREFIX = new RegExp(`^${WORD_DESIGNATOR}`, "i");

/** Unit anywhere after a space or comma: "123 Main St, Suite 200". */
const SECONDARY_UNIT_ANYWHERE = new RegExp(
  `(?:^|[\\s,])(${UNIT_ONLY_DESIGNATOR}${DESIGNATOR_GAP}${UNIT_IDENTIFIER})`,
  "i",
);

/** A leading designator means the line is a sub-address, not a street. */
export function startsWithUnitDesignator(
  value: string | null | undefined,
): boolean {
  return SECONDARY_UNIT_PREFIX.test(value?.trim() ?? "");
}

/** Unit identifiers carry a digit ("APT 13C") or stand alone ("UNIT A"). */
function looksLikeUnitIdentifier(segment: string): boolean {
  return addressTokens(segment)
    .filter((token) => !UNIT_DESIGNATORS.has(token))
    .every((token) => /\d/.test(token) || token.length === 1);
}

/** The unit portion of a single-line street, wherever it sits. */
export function matchSecondaryUnit(
  value: string | null | undefined,
): string | undefined {
  const segment = SECONDARY_UNIT_ANYWHERE.exec(value?.trim() ?? "")?.[1];
  if (!segment || !looksLikeUnitIdentifier(segment)) {
    return undefined;
  }
  return segment.trim();
}

/** Words that pad out a form label but carry no address information. */
const PLACEHOLDER_FILLER = new Set(["ETC", "OPTIONAL", "OR"]);

const PLACEHOLDER_LINES = new Set([
  "ADDRESS 2",
  "ADDRESS 2 OPTIONAL",
  "ADDRESS LINE 2",
  "LINE 2",
  "N A",
  "NA",
  "NONE",
  "STREET 2",
  "STREET ADDRESS 2",
  "X",
]);

/** True for punctuation, "N/A", or an unfilled label like "Apt, suite, etc.". */
export function isPlaceholderAddressLine(
  value: string | null | undefined,
): boolean {
  if (!value?.trim()) {
    return false;
  }
  const tokens = addressTokens(value.trim());
  if (tokens.length === 0 || PLACEHOLDER_LINES.has(tokens.join(" "))) {
    return true;
  }
  return tokens.every(
    (token) => UNIT_DESIGNATORS.has(token) || PLACEHOLDER_FILLER.has(token),
  );
}

/** The trailing unit of `inputStreet1` when neither standardized line kept it. */
export function droppedUnitSuffix(
  inputStreet1: string | null | undefined,
  street1: string,
  street2: string,
): string {
  const segment = SECONDARY_UNIT_SUFFIX.exec(inputStreet1?.trim() ?? "")?.[1];
  if (!segment) {
    return "";
  }
  const identifier = addressTokens(segment).filter(
    (token) => !UNIT_DESIGNATORS.has(token),
  );
  if (identifier.length === 0) {
    return "";
  }
  const retained = new Set([
    ...addressTokens(street1).slice(1),
    ...addressTokens(street2),
  ]);
  return identifier.every((token) => retained.has(token)) ? "" : segment.trim();
}
